import { z } from 'zod';
import { ApiError } from './errors.js';
/**
 * Pickup reservations (`docs/lovespa-gaps-plan.md` C1) — the vocabulary, the
 * lifecycle and the arithmetic. No I/O, no SQL, no HTTP.
 *
 * ## The sentence this file exists for
 *
 * The owner of `docs/lovespa-discovery.md` §4, verbatim: **"I never want to have
 * money that I haven't earned yet."** She sells a professional skincare line off
 * a shelf, her locals want to order it online and collect it, and the guest shop
 * this platform had was *shipped-and-prepaid only* — a Printful quote and a
 * PayPal link. "A shopping cart that doesn't have delivery" is what she asked
 * for, and an **unpaid reservation** is the only version of it consistent with
 * that sentence.
 *
 * So a reservation is a promise about **stock**, not about money: the shop holds
 * a jar on a shelf with somebody's name on it, tells them when it is ready, and
 * the till records the money when they walk in. §6.5's distinction survives
 * intact — this is neither a settlement (money for something delivered) nor a
 * payment of any kind. It is the thing that happens before either.
 *
 * ## Why it is not an `orders` row
 *
 * The long form is in the migration, because that is the file a reviewer reads
 * when they ask why a table exists. The short form: `order_status` is a **wire
 * enum** — `packages/contracts` carries it and the partner agreement names its
 * values — and a pickup reservation needs a state (`expired`) and a lane the
 * agreement never agreed to. Widening a partner's vocabulary to serve a spa's
 * shelf is the one change dev.md §6 says never to make by editing a guest
 * feature.
 *
 * What *is* reused is every discipline: the server prices it (B7), the lines are
 * snapshots, the totals are checked by the database, the transition table lives
 * here as one readable rule, and the events ride the outbox.
 */
/* ------------------------------------------------------------------ */
/* The lifecycle                                                       */
/* ------------------------------------------------------------------ */
/**
 * `placed → ready → collected`, with `cancelled` beside them and `expired` at
 * the end of the shelf's patience.
 *
 * Five states, and each is a different sentence somebody says out loud:
 *
 *  - `placed` — the guest asked, and the stock is held for them from this
 *    instant. Nothing has been paid and nothing has been promised about *when*.
 *  - `ready` — it is on the shelf with their name on it. This is the state a
 *    text message is about ("your order is ready"), which is why it is a state
 *    and not a flag: the message is a consequence of a transition, and a
 *    transition is the only thing that happens exactly once.
 *  - `collected` — they came in, it was handed over, and the till has the money
 *    (or does not, and that is the owner's business — see `payment`).
 *  - `cancelled` — they changed their mind, or the shop did. The hold is
 *    released.
 *  - `expired` — nobody came. The hold is released, and the row stays, because
 *    "she ordered it and never collected it" is a fact about a client that an
 *    owner asks about later.
 */
export const ReservationStatus = z.enum(['placed', 'ready', 'collected', 'cancelled', 'expired']);
/**
 * The two live states — the ones that **hold stock**.
 *
 * Named as a set because every stock question in `packages/store` is asked
 * against it, and a second list of "which statuses count as held" written
 * inline in a query is the drift that makes a shelf's arithmetic wrong.
 */
export const RESERVATION_OPEN_STATUSES = ['placed', 'ready'];
export function reservationHoldsStock(status) {
    return RESERVATION_OPEN_STATUSES.includes(status);
}
/**
 * What may become what.
 *
 * A table rather than a chain of `if`s, for `ORDER_TRANSITIONS`' reason: it is
 * the *whole* rule and a reader should see all of it at once, and a property
 * test can walk it.
 *
 * Three decisions worth reading twice:
 *
 *  * **`placed → collected` is legal**, and it is the one rung that differs from
 *    the kitchen's shape. `ready` is what the *message* is about; a guest who
 *    walks in before the message was sent is still a guest standing at the
 *    counter with their jar in the owner's hand. Making them wait for a tap on
 *    another screen would be the software inventing a queue.
 *  * **`expired` only follows `ready`.** Expiry is defined as *N days after it
 *    was ready* (`shop_pickup_expiry_days`), so a `placed` reservation has no
 *    due date to be past — what it has is a shop that has not got the stock in
 *    yet, and the answer to that is a person cancelling it, not a clock.
 *  * **Nothing leaves a terminal state.** An un-cancelled reservation would be a
 *    hold that reappeared on a shelf somebody has already sold from.
 */
export const RESERVATION_TRANSITIONS = {
    placed: ['ready', 'collected', 'cancelled'],
    ready: ['collected', 'cancelled', 'expired'],
    collected: [],
    cancelled: [],
    expired: [],
};
export function canTransitionReservation(from, to) {
    return RESERVATION_TRANSITIONS[from].includes(to);
}
export function reservationStatusIsTerminal(status) {
    return RESERVATION_TRANSITIONS[status].length === 0;
}
/**
 * The guarded move, as the one refusal every surface raises.
 *
 * `409` for `assertOrderTransition`'s reason: the caller asked for something
 * that was true a moment ago or would be true in another order. Two staff phones
 * tapping "collected" on the same reservation is the normal case, not an attack,
 * and the second one should be told what the state *is*.
 */
export function assertReservationTransition(from, to) {
    if (canTransitionReservation(from, to))
        return;
    throw ApiError.conflict(reservationStatusIsTerminal(from)
        ? `That reservation is already ${from}, and nothing follows it.`
        : `A reservation that is ${from} cannot become ${to}.`, {
        reason: reservationStatusIsTerminal(from) ? 'terminal' : 'wrong_status',
        status: from,
        requested: to,
        allowed: RESERVATION_TRANSITIONS[from],
    });
}
/* ------------------------------------------------------------------ */
/* How it gets paid for                                                */
/* ------------------------------------------------------------------ */
/**
 * **Where the money happens**, snapshotted onto the row at the moment it is
 * made rather than read back off the tenant's config.
 *
 *  - `at_pickup` — nothing online. The guest reserves, collects, and pays at the
 *    desk in whatever the shop takes (cash, check, Venmo, PayPal — recorded at
 *    the till, processed by nobody: `docs/lovespa-discovery.md` §4).
 *  - `prepay` — the ordinary pay link, for a shop that would rather have the
 *    money before it puts a name on a jar.
 *
 * A column rather than a lookup, because `tenant_config` is a *current* answer
 * and this is a *historical* one: a shop that switches to prepay in March must
 * not turn February's unpaid promises into debts, and a guest who was told "pay
 * when you collect" was told that once.
 */
export const ReservationPayment = z.enum(['at_pickup', 'prepay']);
/* ------------------------------------------------------------------ */
/* What a caller may ask for                                           */
/* ------------------------------------------------------------------ */
/**
 * One line of a reservation: **an item id and a count.**
 *
 * There is no `unit_price_cents` here and there never may be one — the same
 * absence `OrderLineRequest` is built around, and the same reason (B7, §3.6's
 * generalization). A shelf item has no modifiers and no notes: a jar of cleanser
 * is a jar of cleanser, and the place for "can you gift-wrap it" is the
 * reservation's own note.
 */
export const ReservationLineRequest = z.object({
    item_id: z.string().min(1).max(128),
    qty: z.number().int().min(1).max(20),
});
/** A basket. Empty is refused: a reservation for nothing is not a reservation. */
export const ReservationBasket = z.array(ReservationLineRequest).min(1).max(20);
/**
 * Price a basket against the shelf, refusing anything that is not on it.
 *
 * Every refusal **names the product**, for `priceOrder`'s reason: the reader is
 * a person holding a phone, and "invalid request" is not a sentence anybody can
 * act on. "There is one of those left" is.
 *
 * ### Which code, and why
 *
 * `invalid` (400) is a request that **could never have worked**: an id that is
 * not on this shop's shelf, an item the owner retired, an open-price line that
 * no untrusted surface may name. `conflict` (409) is a request that **would have
 * worked yesterday**: there are two left and somebody asked for three. That is
 * the agreement's meaning of the two codes and it is the same split
 * `priceOrder` makes between an unknown dish and an 86'd one.
 *
 * ### Untracked stock is not zero stock
 *
 * `available === null` means nobody counts this item, which is what almost every
 * shelf in this fleet is (`item_retail.stock` was untyped for exactly that
 * reason). It reserves freely. The way an owner stops that is `active = false`,
 * which is a statement she makes rather than a number she forgot to maintain —
 * and `dev.md`'s companion rule applies: an absent count is an absent fact, and
 * defaulting it to zero would turn "I never counted these" into "we have none".
 */
export function priceReservation(shelf, basket) {
    if (basket.length === 0) {
        throw ApiError.invalid('A reservation needs at least one item.', { reason: 'empty_order' });
    }
    const lines = [];
    /** Two lines naming one item are one hold; the count has to see both. */
    const asked = new Map();
    for (const [index, requested] of basket.entries()) {
        const where = { line: index, item_id: requested.item_id };
        if (!Number.isInteger(requested.qty) || requested.qty < 1) {
            throw ApiError.invalid('A line is for one item or more.', {
                ...where,
                reason: 'bad_quantity',
                qty: requested.qty,
            });
        }
        const item = shelf.get(requested.item_id);
        if (item === undefined) {
            throw ApiError.invalid('That is not something this shop sells.', {
                ...where,
                reason: 'unknown_item',
            });
        }
        if (item.openPrice || item.onWebsite === false) {
            // Unreachable from the shop projection, which filters these out — so
            // reaching it means a hand-written request naming an id from somewhere
            // else. Named rather than silently dropped (migration 0038's own note:
            // "no untrusted surface may see one of these items at all").
            throw ApiError.invalid('That is not something this shop sells.', {
                ...where,
                reason: 'unknown_item',
            });
        }
        if (!item.active) {
            throw ApiError.invalid(`${item.name} is no longer on sale.`, {
                ...where,
                reason: 'not_on_sale',
                item_name: item.name,
            });
        }
        const total = (asked.get(item.id) ?? 0) + requested.qty;
        asked.set(item.id, total);
        if (item.available !== null && total > item.available) {
            throw ApiError.conflict(item.available <= 0
                ? `${item.name} is out of stock.`
                : `There ${item.available === 1 ? 'is' : 'are'} only ${String(item.available)} of ${item.name} left.`, {
                ...where,
                reason: 'out_of_stock',
                item_name: item.name,
                available: item.available,
                asked: total,
            });
        }
        lines.push({
            itemId: item.id,
            itemName: item.name,
            qty: requested.qty,
            unitPriceCents: item.priceCents,
            lineTotalCents: item.priceCents * requested.qty,
        });
    }
    return {
        lines,
        subtotalCents: lines.reduce((sum, line) => sum + line.lineTotalCents, 0),
    };
}
/**
 * What is still to collect at the desk — the number a prepay link is priced at,
 * and the number the till starts from.
 *
 * One function rather than a subtraction at each call site, for
 * `orderOutstandingCents`' reason: two subtractions eventually disagree about
 * whether a part-paid reservation owes the rest or the whole, and the
 * disagreement is money.
 */
export function reservationOutstandingCents(reservation) {
    return Math.max(0, reservation.totalCents - reservation.prepaidCents);
}
/**
 * When a reservation that became ready today stops being held.
 *
 * Computed at the moment of `ready` and **stored**, rather than compared against
 * the config at read time. The config is a current answer and a deadline is a
 * promise: an owner who shortens the window in June must not retroactively
 * expire May's shelf, and a guest who was told "we'll hold it a week" was told
 * that once. `photo_album_status`' argument, one noun over.
 */
export function reservationExpiryAt(readyAt, holdDays) {
    return new Date(readyAt.getTime() + holdDays * 86_400_000);
}
//# sourceMappingURL=reservations.js.map